import { Box, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { actualsSectionHeadingSx } from './actualsPresentation';

export type ApprovalArchive = {
  id: number;
  approvedBy: string;
  approvedAt: string;
  grandTotal: number;
  snapshot: {
    event: { IANATimeZone?: string };
    lines: { id: number; shiftId?: number; position: string; quantity: number; rate: number; total: number }[];
  };
};

export function approvedShiftIds(records: ApprovalArchive[]): Set<number> {
  return new Set(records.flatMap(record => record.snapshot.lines
    .filter(line => line.shiftId != null).map(line => Number(line.shiftId))));
}

export const approvedActualsWarning = 'This shift is covered by approved client actuals. Edits change current actuals; the archived approval stays unchanged. Review and resend actuals if needed.';

export function approvalTime(value: string, zone?: string): string {
  const date = new Date(value);
  if (!Number.isFinite(date.valueOf())) return 'Approval time unavailable';
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
    hour12: true, timeZoneName: 'short', timeZone: zone || 'UTC'
  };
  try { return new Intl.DateTimeFormat('en-US', options).format(date); }
  catch { return new Intl.DateTimeFormat('en-US', { ...options, timeZone: 'UTC' }).format(date); }
}

export default function ClientActualsArchive({ records }: { records: ApprovalArchive[] }) {
  return <Box component="section" sx={{ px: 2, flex: '1 1 0', minWidth: 320 }} aria-label="Client Actuals Archive">
    <Typography component="h2" variant="h5" sx={actualsSectionHeadingSx}>Client Actuals Archive</Typography>
    {records.length === 0 ? <Typography variant="body2">No archived approvals.</Typography> :
      <Table size="small" aria-label="Approved Client Actuals">
        <TableHead><TableRow><TableCell>Approved By</TableCell><TableCell>Approved At</TableCell><TableCell align="right">Total</TableCell><TableCell>Line Snapshot</TableCell></TableRow></TableHead>
        <TableBody>{records.map(record => <TableRow key={record.id}>
          <TableCell>{record.approvedBy}</TableCell>
          <TableCell>{approvalTime(record.approvedAt, record.snapshot.event.IANATimeZone)}</TableCell>
          <TableCell align="right">${record.grandTotal.toFixed(2)}</TableCell>
          <TableCell><details><summary>{record.snapshot.lines.length} Lines</summary>
            <Table size="small" aria-label={`Approval ${record.id} Lines`}>
              <TableHead><TableRow><TableCell>Position</TableCell><TableCell>Quantity</TableCell><TableCell>Rate</TableCell><TableCell>Total</TableCell></TableRow></TableHead>
              <TableBody>{record.snapshot.lines.map(line => <TableRow key={line.id}>
                <TableCell>{line.position}</TableCell><TableCell>{line.quantity}</TableCell><TableCell>${line.rate.toFixed(2)}</TableCell><TableCell>${line.total.toFixed(2)}</TableCell>
              </TableRow>)}</TableBody>
            </Table>
          </details></TableCell>
        </TableRow>)}</TableBody>
      </Table>}
  </Box>;
}
